/**
 * EntityDetailHeader — the single, shared identity header for an entity
 * detail panel: a type-coloured icon avatar + display name + localized type.
 *
 * One implementation so every surface that shows an entity (the search entity
 * sheet, the research-citation sheet, the deep-research drill-in) reads
 * identically. Colours come from the entity's type, so two different types
 * (e.g. Person vs EmailAddress) legitimately differ — same component, not a
 * style fork.
 */

import { useTranslation } from 'react-i18next'
import { ENTITY_ICON_COMPONENTS, LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'

export function EntityDetailHeader({
  name,
  typeLabel,
}: {
  name: string
  /** Primary entity label (Person, EmailAddress, …); drives icon + colour. */
  typeLabel?: string | null
}) {
  const { t } = useTranslation()
  const Icon = ENTITY_ICON_COMPONENTS[typeLabel ?? ''] ?? ENTITY_ICON_COMPONENTS.Default
  const color = LABEL_COLORS[typeLabel ?? ''] ?? LABEL_COLORS.Default
  const type = typeLabel ? t(`entityType.${typeLabel}`, { defaultValue: typeLabel }) : ''

  return (
    <div className="flex items-center gap-3">
      <span
        className="flex size-10 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color.fill}1a`, color: color.fill }}
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-base font-semibold leading-tight text-foreground">{name}</h2>
        {type ? (
          <p className="mt-0.5 text-2xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {type}
          </p>
        ) : null}
      </div>
    </div>
  )
}
